import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts'
import { CircularProgress } from './CircularProgress'
import { TabProps } from './types'
import { useFormatters, usePreferences } from '../../contexts/PreferencesContext'

export function GroupKPITab(props: TabProps) {
  const { formatCurrency, formatNumber } = useFormatters()
  // OTIF/KPI percentage decimals from Admin Setup -> Regional -> Decimal
  // Precision. 0 keeps whole-number ring labels; 1-2 shows e.g. 8.6%.
  const { preferences } = usePreferences()
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0

  // Delivery metric mode: conditionally show OTIF and/or OTD sections
  const deliveryMode = props.data.delivery_metric_mode ?? 'both'
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only'
  const showOTD = deliveryMode === 'both' || deliveryMode === 'otd_only'

  const groupKpi = props.data.groupKpi;

  // CR-4211: Use configurable targets from API with fallbacks
  const otifTarget = props.data.targets?.otif ?? props.data.quality?.otifTarget ?? 95 // Q46: field default
  const otdTarget = props.data.targets?.otd ?? props.data.delivery?.otdTarget ?? 90
  const ppmTarget = props.data.targets?.ppm ?? props.data.quality?.ppmTarget ?? 1500

  // Get color based on performance
  const getPerformanceColor = (value: number, target: number, inverse: boolean = false) => {
    if (inverse) {
      // For PPM, lower is better
      if (value <= target) return '#10b981'; // green
      if (value <= target * 1.5) return '#ff9800'; // orange
      return '#ef4444'; // red
    } else {
      // For OTIF/OTD, higher is better
      if (value >= target) return '#10b981'; // green
      if (value >= target * 0.8) return '#ff9800'; // orange
      return '#ef4444'; // red
    }
  };

  if (!groupKpi) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No group KPI data available</p>
      </div>
    );
  }

  const external = groupKpi.external;
  const internal = groupKpi.internal;

  return (
    <div className="space-y-4">
      {/* OTIF Section */}
      {showOTIF && (
      <div className="grid grid-cols-2 gap-4">
        {/* Exc Group Companies OTIF */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">OTIF - Exc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {external ? (
              <>
                <div className="grid grid-cols-2 gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">MONTH</p>
                    <CircularProgress
                      percentage={external.month_otif}
                      color={getPerformanceColor(external.month_otif ?? 0, otifTarget)}
                      size={80}
                      strokeWidth={8}
                      decimals={kpiDecimals}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">FYTD</p>
                    <CircularProgress
                      percentage={external.fytd_otif}
                      color={getPerformanceColor(external.fytd_otif ?? 0, otifTarget)}
                      size={80}
                      strokeWidth={8}
                      decimals={kpiDecimals}
                    />
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={external.otif_trend}>
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={50} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Area type="monotone" dataKey="otif" stroke="#3b82f6" fill="#fce4ec" />
                    <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">No external supplier data</p>
            )}
          </CardContent>
        </Card>

        {/* Inc Group Companies OTIF */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">OTIF - Inc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {internal ? (
              <>
                <div className="grid grid-cols-2 gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">MONTH</p>
                    <CircularProgress
                      percentage={internal.month_otif}
                      color={getPerformanceColor(internal.month_otif ?? 0, otifTarget)}
                      size={80}
                      strokeWidth={8}
                      decimals={kpiDecimals}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">FYTD</p>
                    <CircularProgress
                      percentage={internal.fytd_otif}
                      color={getPerformanceColor(internal.fytd_otif ?? 0, otifTarget)}
                      size={80}
                      strokeWidth={8}
                      decimals={kpiDecimals}
                    />
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={internal.otif_trend}>
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={50} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Area type="monotone" dataKey="otif" stroke="#3b82f6" fill="#fce4ec" />
                    <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">No internal supplier data</p>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* OTD Section */}
      {showOTD && (
      <div className="grid grid-cols-2 gap-4">
        {/* OTD Exc Group */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">OTD - Exc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {external ? (
              <>
                <div className="grid grid-cols-2 gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">MONTH</p>
                    <CircularProgress
                      percentage={external.month_otd}
                      color={getPerformanceColor(external.month_otd, otdTarget)}
                      size={80}
                      strokeWidth={8}
                      decimals={kpiDecimals}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">FYTD</p>
                    <CircularProgress
                      percentage={external.fytd_otd}
                      color={getPerformanceColor(external.fytd_otd, otdTarget)}
                      size={80}
                      strokeWidth={8}
                      decimals={kpiDecimals}
                    />
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={external.otd_trend}>
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={50} />
                    <YAxis domain={[30, 100]} allowDataOverflow={true} tick={{ fontSize: 10 }} />
                    <Area type="monotone" dataKey="otd" stroke="#3b82f6" fill="#fce4ec" />
                    <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">No external supplier data</p>
            )}
          </CardContent>
        </Card>

        {/* OTD Inc Group */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">OTD - Inc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {internal ? (
              <>
                <div className="grid grid-cols-2 gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">MONTH</p>
                    <CircularProgress
                      percentage={internal.month_otd}
                      color={getPerformanceColor(internal.month_otd, otdTarget)}
                      size={80}
                      strokeWidth={8}
                      decimals={kpiDecimals}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">FYTD</p>
                    <CircularProgress
                      percentage={internal.fytd_otd}
                      color={getPerformanceColor(internal.fytd_otd, otdTarget)}
                      size={80}
                      strokeWidth={8}
                      decimals={kpiDecimals}
                    />
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={internal.otd_trend}>
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={50} />
                    <YAxis domain={[30, 100]} allowDataOverflow={true} tick={{ fontSize: 10 }} />
                    <Area type="monotone" dataKey="otd" stroke="#3b82f6" fill="#fce4ec" />
                    <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">No internal supplier data</p>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* PPM Section */}
      <div className="grid grid-cols-2 gap-4">
        {/* PPM Exc Group */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">PPM - Exc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {external ? (
              <>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">MONTH</p>
                    <div className="text-2xl font-bold">{formatNumber(external.month_ppm)}</div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">FYTD</p>
                    <div className={`${external.fytd_ppm > ppmTarget ? 'bg-destructive text-destructive-foreground' : 'bg-green-600 text-white'} px-3 py-2 rounded-md`}>
                      <p className="text-lg font-bold">{formatNumber(external.fytd_ppm)}</p>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={external.ppm_trend}>
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Area type="monotone" dataKey="ppm" stroke="#3b82f6" fill="#93c5fd" />
                    <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">No external supplier data</p>
            )}
          </CardContent>
        </Card>

        {/* PPM Inc Group */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">PPM - Inc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {internal ? (
              <>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">MONTH</p>
                    <div className="text-2xl font-bold">{formatNumber(internal.month_ppm)}</div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2">FYTD</p>
                    <div className={`${internal.fytd_ppm > ppmTarget ? 'bg-destructive text-destructive-foreground' : 'bg-green-600 text-white'} px-3 py-2 rounded-md`}>
                      <p className="text-lg font-bold">{formatNumber(internal.fytd_ppm)}</p>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={internal.ppm_trend}>
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Area type="monotone" dataKey="ppm" stroke="#3b82f6" fill="#93c5fd" />
                    <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">No internal supplier data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Arrears and Spend Section */}
      <div className="grid grid-cols-2 gap-4">
        {/* Arrears Exc Group */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">ARREARS - Exc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {external ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-2">VALUE</p>
                  <div className="text-2xl font-bold">{formatCurrency(external.arrears_value)}</div>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-2">PO LINES</p>
                  <div className="text-2xl font-bold">{external.arrears_count}</div>
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">No external supplier data</p>
            )}
          </CardContent>
        </Card>

        {/* Arrears Inc Group */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">ARREARS - Inc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {internal ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-2">VALUE</p>
                  <div className="text-2xl font-bold">{formatCurrency(internal.arrears_value)}</div>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-2">PO LINES</p>
                  <div className="text-2xl font-bold">{internal.arrears_count}</div>
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">No internal supplier data</p>
            )}
          </CardContent>
        </Card>

        {/* Total Spend and Components Exc Group */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">Exc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {external ? (
              <div className="space-y-3">
                <div className="bg-muted rounded-md p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">TOTAL SPEND</span>
                    <span className="text-lg font-bold">{formatCurrency(external.total_spend)}</span>
                  </div>
                </div>
                <div className="bg-muted rounded-md p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">COMPONENTS DELD</span>
                    <span className="text-lg font-bold">{formatNumber(external.components_delivered)}</span>
                  </div>
                </div>
                <div className="bg-muted rounded-md p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">COMPONENTS REJD</span>
                    <span className="text-lg font-bold">{formatNumber(external.components_rejected)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">No external supplier data</p>
            )}
          </CardContent>
        </Card>

        {/* Total Spend and Components Inc Group */}
        <Card className="shadow-sm">
          <CardHeader className="bg-muted/50 py-3">
            <CardTitle className="text-sm font-medium text-center">Inc Group companies</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {internal ? (
              <div className="space-y-3">
                <div className="bg-muted rounded-md p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">TOTAL SPEND</span>
                    <span className="text-lg font-bold">{formatCurrency(internal.total_spend)}</span>
                  </div>
                </div>
                <div className="bg-muted rounded-md p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">COMPONENTS DELD</span>
                    <span className="text-lg font-bold">{formatNumber(internal.components_delivered)}</span>
                  </div>
                </div>
                <div className="bg-muted rounded-md p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">COMPONENTS REJD</span>
                    <span className="text-lg font-bold">{formatNumber(internal.components_rejected)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">No internal supplier data</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
